import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { TooltipModule } from 'primeng/tooltip';
import {
  PROCESS_GROUPS,
  ProcessGroupDefinition,
  ProcessGroupFilterId,
  ProcessGroupId,
} from '@app/pages/admin/orchestration-process-groups.util';
import {
  ScheduleGanttInput,
  ScheduleGanttWindowHours,
  buildScheduleGanttView,
} from './subscription-schedule-gantt.util';

interface GanttTickView {
  atMs: number;
  leftPercent: number;
  label: string;
}

interface GanttMarkView {
  leftPercent: number;
  widthPercent: number;
  overlapping: boolean;
  dense: boolean;
  tooltip: string;
}

interface GanttRowView {
  id: string;
  name: string;
  processLabel: string;
  groupId: ProcessGroupId;
  detailsUrl: string | null;
  isActive: boolean;
  usesNextFireOnly: boolean;
  marks: GanttMarkView[];
}

interface GanttGroupSectionView {
  id: ProcessGroupId;
  labelKey: string;
  icon: string;
  rows: GanttRowView[];
  activeSubscriptionCount: number;
}

@Component({
  selector: 'app-subscription-schedule-gantt',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, TooltipModule, SecondaryButton],
  templateUrl: './subscription-schedule-gantt.html',
  styleUrl: './subscription-schedule-gantt.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionScheduleGanttComponent {
  readonly items = input<ScheduleGanttInput[]>([]);
  readonly now = input<Date>(new Date());
  readonly groupChips = input<ProcessGroupDefinition[]>([]);
  readonly selectedGroup = input<ProcessGroupFilterId>('all');
  readonly selectedGroupChange = output<ProcessGroupFilterId>();
  readonly windowHours = input<ScheduleGanttWindowHours>(24);
  readonly windowHoursChange = output<ScheduleGanttWindowHours>();
  readonly moduleActiveCounts = input<Partial<Record<ProcessGroupId, number>>>({});
  readonly disablingModule = input<ProcessGroupId | null>(null);
  readonly disableModule = output<ProcessGroupId>();
  readonly timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  readonly windowChips: { id: ScheduleGanttWindowHours; label: string }[] = [
    { id: 24, label: 'Next 24 hours' },
    { id: 168, label: 'Next 7 days' },
  ];

  private readonly chart = computed(() =>
    buildScheduleGanttView(this.items(), this.windowHours(), this.now()),
  );

  readonly overlapCount = computed(() => this.chart().overlapCount);
  readonly empty = computed(() => this.chart().rows.length === 0);
  readonly noStarts = computed(
    () => this.rows().length > 0 && this.rows().every(row => row.marks.length === 0),
  );
  readonly ticks = computed<GanttTickView[]>(() => {
    const hours = this.windowHours();
    return this.chart().axisTicks.map(tick => ({
      ...tick,
      label: this.formatTick(tick.atMs, hours),
    }));
  });
  readonly rows = computed<GanttRowView[]>(() =>
    this.chart().rows.map(row => ({
      id: row.id,
      name: row.name,
      processLabel: row.processLabel,
      groupId: row.groupId,
      detailsUrl: row.detailsUrl,
      isActive: row.isActive,
      usesNextFireOnly: row.usesNextFireOnly,
      marks: row.marks.map(mark => ({
        leftPercent: mark.leftPercent,
        widthPercent: mark.widthPercent,
        overlapping: mark.overlapping,
        dense: mark.dense,
        tooltip: this.formatMarkTooltip(mark.atMs, mark.endMs, mark.dense),
      })),
    })),
  );
  readonly groupSections = computed<GanttGroupSectionView[]>(() => {
    const defs = new Map(PROCESS_GROUPS.map(group => [group.id, group]));
    const sections: GanttGroupSectionView[] = [];
    for (const row of this.rows()) {
      const last = sections[sections.length - 1];
      if (!last || last.id !== row.groupId) {
        const def = defs.get(row.groupId);
        sections.push({
          id: row.groupId,
          labelKey: def?.labelKey ?? 'Other',
          icon: def?.icon ?? 'pi pi-box',
          rows: [row],
          activeSubscriptionCount: this.moduleActiveCounts()[row.groupId] ?? 0,
        });
      } else {
        last.rows.push(row);
      }
    }
    return sections;
  });

  selectWindow(hours: ScheduleGanttWindowHours): void {
    if (hours === this.windowHours()) {
      return;
    }
    this.windowHoursChange.emit(hours);
  }

  selectGroup(groupId: ProcessGroupFilterId): void {
    if (groupId === this.selectedGroup()) {
      return;
    }
    this.selectedGroupChange.emit(groupId);
  }

  private formatTick(atMs: number, hours: ScheduleGanttWindowHours): string {
    const date = new Date(atMs);
    if (hours === 168) {
      return date.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    }
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }

  private formatMarkTooltip(atMs: number, endMs: number, dense: boolean): string {
    const start = new Date(atMs).toLocaleString();
    if (!dense) {
      return start;
    }
    return `${start} – ${new Date(endMs).toLocaleString()}`;
  }
}
